import {
  DEFAULT_NEWS_SORT,
  EMPTY_NEWS_FILTER,
  type NewsFilter,
  type NewsSortOrder,
} from './newsFilters';

/**
 * "How I read my news" — one business's filter and sort, kept on this device.
 *
 * Same manners as `lib/draftStorage.ts`: a versioned envelope, every read and
 * write wrapped, and silence on failure. `localStorage` throws on quota and in
 * some private-browsing modes, and a payload may have been written by an older
 * build — neither is worth costing the user a screen, so both end in "no
 * default" rather than an error.
 *
 * Its own namespace rather than `outpost:draft:` because this is not typed work
 * being rescued: it is a preference the user asked to keep, and the draft
 * sweeper must never take it.
 */

/** Bumped when the stored shape's meaning changes. Older payloads are dropped. */
const PREFS_VERSION = 1;

const PREFS_PREFIX = 'outpost:news:filters';

/** One business's saved default. Scoped by business, like every owned record. */
export function newsFilterPrefsKey(businessId: string): string {
  return `${PREFS_PREFIX}:${businessId}`;
}

/** What "my default" means: the filter and the order, together. */
export interface NewsFilterPrefs {
  filter: NewsFilter;
  sort: NewsSortOrder;
}

export const DEFAULT_NEWS_PREFS: NewsFilterPrefs = {
  filter: EMPTY_NEWS_FILTER,
  sort: DEFAULT_NEWS_SORT,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A stored id list: an array of non-empty strings, or nothing usable. */
function readIds(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const ids: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string' || entry.length === 0) return null;
    ids.push(entry);
  }
  return ids;
}

/**
 * Narrows a parsed payload to a filter, field by field.
 *
 * Nothing is repaired: a payload missing a field or holding the wrong kind of
 * value is not this shape, and the whole default is discarded. Ids are *not*
 * checked against the user's sources here — that is `pruneNewsFilter`'s job,
 * and it runs against live data the moment the page has it.
 */
function readFilter(value: unknown): NewsFilter | null {
  if (!isPlainObject(value)) return null;
  if (typeof value.industryOnly !== 'boolean' || typeof value.anyCategory !== 'boolean') {
    return null;
  }
  const categoryIds = readIds(value.categoryIds);
  const sourceIds = readIds(value.sourceIds);
  if (categoryIds === null || sourceIds === null) return null;
  return {
    industryOnly: value.industryOnly,
    anyCategory: value.anyCategory,
    categoryIds,
    sourceIds,
  };
}

function readSort(value: unknown): NewsSortOrder | null {
  return value === 'newest' || value === 'oldest' ? value : null;
}

/**
 * The saved default for this business, or null when there is none to apply.
 *
 * A payload that fails the envelope or the shape check is swept rather than
 * returned, so a build that changes the filter's meaning cleans up after the
 * one before it.
 */
export function readNewsFilterPrefs(businessId: string | null): NewsFilterPrefs | null {
  if (businessId === null) return null;
  const key = newsFilterPrefsKey(businessId);

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (isPlainObject(parsed) && parsed.v === PREFS_VERSION) {
      const data: unknown = parsed.data;
      if (isPlainObject(data)) {
        const filter = readFilter(data.filter);
        const sort = readSort(data.sort);
        if (filter !== null && sort !== null) return { filter, sort };
      }
    }
  } catch {
    // Truncated or hand-edited JSON. Falls through to the sweep below.
  }

  clearNewsFilterPrefs(businessId);
  return null;
}

/** Writes the default. Best effort: a full or disabled store is not an error. */
export function writeNewsFilterPrefs(businessId: string, prefs: NewsFilterPrefs): void {
  try {
    window.localStorage.setItem(
      newsFilterPrefsKey(businessId),
      JSON.stringify({
        v: PREFS_VERSION,
        saved_at: new Date().toISOString(),
        data: { filter: prefs.filter, sort: prefs.sort },
      }),
    );
  } catch {
    // Quota exceeded, storage disabled, private mode. A default is a convenience.
  }
}

/** Forgets the default. The next visit opens on the whole feed, newest first. */
export function clearNewsFilterPrefs(businessId: string): void {
  try {
    window.localStorage.removeItem(newsFilterPrefsKey(businessId));
  } catch {
    // Same story: nothing the user can act on.
  }
}
